import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filter, Observable } from 'rxjs';
import { DriftConfiguration } from '../models/configuration.interface';
import { ConfigurationService } from './configuration.service';

// Interfaces para los modelos
export interface ForwardingCondition {
  condition_id?: string;
  column_name: string;
  operator: 'equals' | 'contains' | 'starts_with';
  value: string;
}

export interface ForwardingRule {
  rule_id: string;
  name: string;
  organization_id: string;
  logic_operator?: 'and' | 'or';
  conditions: ForwardingCondition[];
  target_collection_id: string;
  frequency: 'pastAndFuture' | 'future';
  status: 'active' | 'inactive' | 'paused';
  created_at: string;
  updated_at: string;
  created_by?: string;
  last_executed?: string;
  execution_count: number;
}

export interface CreateForwardingRuleRequest {
  name: string;
  logic_operator?: 'and' | 'or';
  conditions: ForwardingCondition[];
  target_collection_id: string;
  frequency: 'pastAndFuture' | 'future';
  organization_id: string;
}

export interface UpdateForwardingRuleRequest {
  name?: string;
  logic_operator?: 'and' | 'or';
  conditions?: ForwardingCondition[];
  target_collection_id?: string;
  frequency?: 'pastAndFuture' | 'future';
  status?: 'active' | 'inactive' | 'paused';
}

export interface TestRuleRequest {
  test_data: Record<string, unknown>;
}

export interface ConditionResult {
  condition_index: number;
  condition: {
    column_name: string;
    operator: string;
    value: string;
  };
  document_value: unknown;
  matches: boolean;
}

export interface TestRuleResponse {
  rule_id: string;
  rule_name: string;
  logic_operator: string;
  overall_match: boolean;
  condition_results: ConditionResult[];
  test_data: Record<string, unknown>;
}

export interface ExecuteRuleResponse {
  rule_id: string;
  executed_at: string;
  items_processed: number;
  items_forwarded: number;
  status: string;
}

@Injectable({
  providedIn: 'root'
})
export class ForwardingRuleService {
  config: DriftConfiguration;
  baseUrl = '';

  constructor(
    private http: HttpClient,
    private configurationService: ConfigurationService
  ) {
    this.config = {};

    this.configurationService
      .configurationLoaded()
      .pipe(filter(loaded => loaded === true))
      .subscribe(() => {
        this.config = this.configurationService.getConfiguration();
      });

    this.baseUrl = `${this.config.driftApi}/forwarding-rules`;
  }

  /*
   * GET ALL - Obtener todas las reglas (sin filtro de organización)
   * Endpoint: GET /forwarding-rules/all
   */
  getAllForwardingRules(): Observable<ForwardingRule[]> {
    console.log('Llegando al servicio');
    const url = `${this.baseUrl}/all`;
    return this.http.get<ForwardingRule[]>(url);
  }

  /*
   * GET BY ID - Obtener una regla específica
   * Endpoint: GET /forwarding-rules/{rule_id}
   */
  getForwardingRuleById(ruleId: string): Observable<ForwardingRule> {
    const url = `${this.baseUrl}/${ruleId}`;
    return this.http.get<ForwardingRule>(url);
  }

  /*
   * POST - Crear nueva regla
   * Endpoint: POST /forwarding-rules
   */
  createForwardingRule(rule: CreateForwardingRuleRequest): Observable<ForwardingRule> {
    return this.http.post<ForwardingRule>(this.baseUrl, rule);
  }

  /**
   * PUT - Actualizar regla existente
   * Endpoint: PUT /forwarding-rules/{rule_id}
   */
  updateForwardingRule(ruleId: string, updates: UpdateForwardingRuleRequest): Observable<ForwardingRule> {
    const url = `${this.baseUrl}/${ruleId}`;
    return this.http.put<ForwardingRule>(url, updates);
  }

  // /**
  //  * GET - Obtener reglas filtradas por organización
  //  * Endpoint: GET /forwarding-rules?organization_id=xxx
  //  */
  // getForwardingRulesByOrganization(organizationId: string): Observable<ForwardingRule[]> {
  //   const params = new HttpParams().set('organization_id', organizationId);
  //   return this.http.get<ForwardingRule[]>(this.baseUrl, { params });
  // }

  /**
   * DELETE - Eliminar regla (soft delete)
   * Endpoint: DELETE /forwarding-rules/{rule_id}
   */
  deleteForwardingRule(ruleId: string): Observable<{ message: string }> {
    const url = `${this.baseUrl}/${ruleId}`;
    return this.http.delete<{ message: string }>(url);
  }

  /*
   * POST - Probar regla con datos de ejemplo
   * Endpoint: POST /forwarding-rules/{rule_id}/test
   TODO: PENDING TO CHECK
   */
  testForwardingRule(ruleId: string, testData: TestRuleRequest): Observable<TestRuleResponse> {
    const url = `${this.baseUrl}/${ruleId}/test`;
    return this.http.post<TestRuleResponse>(url, testData);
  }

  /*
   * POST - Ejecutar regla manualmente
   * Endpoint: POST /forwarding-rules/{rule_id}/execute
   TODO: PENDING TO CHECK
   */
  executeForwardingRule(ruleId: string): Observable<ExecuteRuleResponse> {
    const url = `${this.baseUrl}/${ruleId}/execute`;
    return this.http.post<ExecuteRuleResponse>(url, {});
  }

  // /**
  //  * GET con manejo de errores específico
  //  */
  // getForwardingRuleByIdWithErrorHandling(ruleId: string): Observable<ForwardingRule> {
  //   const url = `${this.baseUrl}/${ruleId}`;
  //   return this.http
  //     .get<ForwardingRule>(url)
  //     .pipe
  //     // Aquí podrías agregar operators de RxJS para manejo de errores
  //     // catchError, retry, timeout, etc.
  //     ();
  // }
}
